import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  clearAuthSession,
  getAuthSession,
  setAuthSession,
  subscribeAuthSession,
} from '../../shared/api/authSessionStore';
import {
  exchangeWorkspaceClerkSession,
  fetchWorkspaceWebSession,
} from '../../shared/api/clerkAuthApi';
import {
  buildWorkspaceCookieSession,
  isAuthenticatedWebSession,
} from '../../shared/api/browserSession';
import { useClerkRuntime } from './ClerkRuntimeProvider';
import { ENABLE_AUTH } from '../../shared/utils/constants';
import { IS_NATIVE_APP } from '../../shared/utils/platform';

const AuthSessionContext = createContext({
  session: null,
  isReady: true,
  setSession: () => {},
  clearSession: () => {},
});

export function useAuthSession() {
  return useContext(AuthSessionContext);
}

function shouldSkipClerkBootstrapForPath(pathname = '') {
  const normalizedPath = String(pathname || '').trim();
  return (
    normalizedPath === '/login' ||
    normalizedPath === '/admin' ||
    normalizedPath === '/admin/login' ||
    normalizedPath.startsWith('/admin/') ||
    normalizedPath === '/accept-invitation' ||
    normalizedPath.startsWith('/accept-invitation/') ||
    normalizedPath === '/customer-portal' ||
    normalizedPath.startsWith('/customer-portal/') ||
    normalizedPath === '/auth/clerk/sign-in' ||
    normalizedPath.startsWith('/auth/clerk/sign-in/')
  );
}

export default function AuthSessionProvider({ children }) {
  const location = useLocation();
  const [session, setSession] = useState(() => getAuthSession());
  const [isReady, setIsReady] = useState(() => {
    if (!ENABLE_AUTH) {
      return true;
    }

    const initialSession = getAuthSession();
    if (initialSession?.accessToken) {
      return true;
    }

    if (IS_NATIVE_APP) {
      return true;
    }

    return typeof window !== 'undefined'
      ? shouldSkipClerkBootstrapForPath(window.location.pathname)
      : true;
  });
  const bootstrapAttemptRef = useRef('');
  const clerkRuntime = useClerkRuntime();
  const hasAccessToken = Boolean(session?.accessToken);
  const currentPathname = location.pathname || '/';
  const shouldSkipBootstrap = IS_NATIVE_APP || shouldSkipClerkBootstrapForPath(currentPathname);

  useEffect(() => {
    const unsubscribe = subscribeAuthSession((nextSession) => {
      setSession(nextSession);
      if (nextSession?.accessToken) {
        setIsReady(true);
      }
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    if (
      clerkRuntime.workspaceEnabled &&
      clerkRuntime.isLoaded &&
      !clerkRuntime.isSignedIn &&
      session?.authSource === 'clerk'
    ) {
      clearAuthSession();
    }
  }, [
    clerkRuntime.isLoaded,
    clerkRuntime.isSignedIn,
    clerkRuntime.workspaceEnabled,
    session?.authSource,
  ]);

  useEffect(() => {
    if (!ENABLE_AUTH) {
      setIsReady(true);
      return undefined;
    }

    if (hasAccessToken) {
      setIsReady(true);
      return undefined;
    }

    if (shouldSkipBootstrap) {
      setIsReady(true);
      return undefined;
    }

    let mounted = true;
    const currentSession = getAuthSession() || {};
    const attemptKey = [
      currentPathname,
      currentSession?.tenantId || '',
      String(clerkRuntime.user?.id || ''),
      String(clerkRuntime.isSignedIn),
      String(clerkRuntime.isLoaded),
    ].join(':');

    if (bootstrapAttemptRef.current === attemptKey) {
      return undefined;
    }

    bootstrapAttemptRef.current = attemptKey;
    setIsReady(false);

    Promise.resolve()
      .then(() => fetchWorkspaceWebSession())
      .then((result) => {
        if (!mounted) {
          return null;
        }

        if (isAuthenticatedWebSession(result)) {
          setAuthSession(buildWorkspaceCookieSession(result, currentSession));
          setIsReady(true);
          return null;
        }

        if (!clerkRuntime.workspaceEnabled) {
          clearAuthSession();
          setIsReady(true);
          return null;
        }

        if (!clerkRuntime.isLoaded) {
          return null;
        }

        if (!clerkRuntime.isSignedIn) {
          clearAuthSession();
          setIsReady(true);
          return null;
        }

        return clerkRuntime
          .getToken()
          .then((clerkToken) => {
            if (!mounted || !clerkToken) {
              return null;
            }

            return exchangeWorkspaceClerkSession({
              clerkToken,
              tenantId: currentSession?.tenantId,
            });
          })
          .then((exchangeResult) => {
            if (!mounted || !isAuthenticatedWebSession(exchangeResult)) {
              clearAuthSession();
              setIsReady(true);
              return;
            }

            setAuthSession(buildWorkspaceCookieSession(exchangeResult, currentSession, 'clerk'));
            setIsReady(true);
          });
      })
      .catch((error) => {
        if (!mounted) {
          return;
        }

        if (error?.code !== 'TENANT_SELECTION_REQUIRED') {
          clearAuthSession();
        }
        setIsReady(true);
      });

    return () => {
      mounted = false;
    };
  }, [
    clerkRuntime,
    currentPathname,
    hasAccessToken,
    shouldSkipBootstrap,
  ]);

  const value = useMemo(
    () => ({
      session,
      isReady,
      setSession: (nextSession) => setAuthSession(nextSession),
      clearSession: () => clearAuthSession(),
    }),
    [isReady, session]
  );

  return <AuthSessionContext.Provider value={value}>{children}</AuthSessionContext.Provider>;
}
